import { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  Fade
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import THEME from '../../../constants/theme';

const CardCarousel = ({
  items = [],
  renderCard,
  title,
  visibleItems = 3,
  compact = false,
  expectedMaxCardHeightPx,
  emptyMessage = 'No items available',
  sx = {},
  spaceForDotsPx = 30 // Default value, can be overridden
}) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const carouselRef = useRef(null);

  // --- Desktop 3D Carousel Logic ---
  const carouselStageHeightPx = compact ? 280 : 450;
  // spaceForDotsPx is now a prop
  const desktopStageMarginBottom =
    Math.max(
      0,
      (expectedMaxCardHeightPx || carouselStageHeightPx) - carouselStageHeightPx
    ) + spaceForDotsPx;

  useEffect(() => {
    setCurrentIndex(0);
  }, [items.length, isMobile]);

  const handleNext = () => {
    if (isAnimating || items.length <= 1) return;
    setIsAnimating(true);
    setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
  };

  const handlePrev = () => {
    if (isAnimating || items.length <= 1) return;
    setIsAnimating(true);
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  };
  const getItemsWithPositions = () => {
    const effectiveVisibleItems = visibleItems;
    return items.map((item, index) => {
      let position = index - currentIndex;
      if (position < -Math.floor(effectiveVisibleItems / 2)) {
        position += items.length;
      } else if (
        position > Math.floor(items.length - effectiveVisibleItems / 2)
      ) {
        position -= items.length;
      }
      const isActive = position === 0;
      // For single items, center them by setting xPercent to 0 regardless of position
      const xPercent = items.length === 1 ? 0 : position * 50;
      // For single items, always use full scale (no size reduction)
      const scale = items.length === 1 ? 1 : isActive ? 1 : 0.85;
      const zIndex = isActive ? 10 : 5 - Math.abs(position);
      // For single items, always use full opacity (no fade effect)
      const opacity =
        items.length === 1
          ? 1
          : isActive
            ? 1
            : Math.abs(position) <= Math.floor(effectiveVisibleItems / 2)
              ? 1 - Math.abs(position) * 0.2
              : 0;
      return {
        item,
        position,
        style: { xPercent, scale, zIndex, opacity, isActive }
      };
    });
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  }; // --- Mobile Scrollable View ---
  if (isMobile) {
    // Show a bit of the next/prev card for affordance
    const cardWidthVw = 76; // LESS than 80vw to show edge of next/prev card
    const spaceBetweenCardsPx = 12; // Reduced gap for more peek
    const sideMarginVw = (100 - cardWidthVw) / 2;

    // Handle empty items
    if (!items || items.length === 0) {
      return (
        <Box sx={{ width: '100%', position: 'relative', ...sx }}>
          {title && (
            <Typography
              variant='h5'
              component='h3'
              sx={{
                mb: 2,
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                color: THEME.COLORS.PRIMARY_BLUE,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem',
              backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT,
              borderRadius: '8px',
              minHeight: '150px'
            }}
          >
            <Typography
              variant='body1'
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                fontSize: '1.1rem',
                textAlign: 'center'
              }}
            >
              {emptyMessage}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', position: 'relative', ...sx }}>
        {title && (
          <Typography
            variant='h5'
            component='h3'
            sx={{
              mb: 2,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
              color: THEME.COLORS.PRIMARY_BLUE,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              paddingLeft: `calc(${sideMarginVw}vw + ${THEME.SPACING.MEDIUM / 2}px)`,
              paddingRight: `calc(${sideMarginVw}vw + ${THEME.SPACING.MEDIUM / 2}px)`
            }}
          >
            {title}
          </Typography>
        )}
        {/* Scroll hint overlay */}
        {/* Scroll hint overlay moved to bottom */}
        <Fade in timeout={600}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              bottom: 12,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 3
            }}
          >
            <Box
              sx={{
                bgcolor: THEME.COLORS.NEUTRAL_WHITE,
                color: THEME.COLORS.PRIMARY_BLUE,
                px: 2,
                py: 0.5,
                borderRadius: 999,
                fontSize: '0.85rem',
                boxShadow: 1,
                opacity: 0.86,
                fontWeight: 600,
                letterSpacing: 0.5
              }}
            >
              <span style={{ verticalAlign: 'middle' }}>
                ← &nbsp;Scroll to see more&nbsp; →
              </span>
            </Box>
          </Box>
        </Fade>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            py: 2,
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none'
          }}
        >
          {items.map((item, index) => (
            <Box
              key={`mobile-card-${index}`}
              sx={{
                flex: `0 0 ${cardWidthVw}vw`,
                width: `${cardWidthVw}vw`,
                scrollSnapAlign: 'center',
                marginRight: `${spaceBetweenCardsPx}px`,
                '&:first-of-type': {
                  marginLeft: `${sideMarginVw}vw`
                },
                '&:last-of-type': {
                  marginRight: `${sideMarginVw}vw`
                },
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {renderCard(item, index, true)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  } // --- Desktop 3D Carousel View ---

  // Handle empty items
  if (!items || items.length === 0) {
    return (
      <Box sx={{ position: 'relative', width: '100%', ...sx }}>
        {title && (
          <Typography
            variant='h5'
            component='h3'
            sx={{
              mb: 2,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
              color: THEME.COLORS.PRIMARY_BLUE,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
            }}
          >
            {title}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem',
            backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT,
            borderRadius: '8px',
            minHeight: '200px',
            marginTop: 2
          }}
        >
          <Typography
            variant='body1'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontSize: '1.2rem',
              textAlign: 'center'
            }}
          >
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', ...sx }}>
      {title && (
        <Typography
          variant='h5'
          component='h3'
          sx={{
            mb: 2,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
            color: THEME.COLORS.PRIMARY_BLUE,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
          }}
        >
          {' '}
          {title}{' '}
        </Typography>
      )}
      <Box
        ref={carouselRef}
        sx={{
          position: 'relative',
          height: `${carouselStageHeightPx}px`,
          width: '100%',
          perspective: '1200px',
          overflow: 'visible',
          mt: 2,
          marginBottom: `${desktopStageMarginBottom}px`
        }}
      >
        {' '}
        {getItemsWithPositions().map(({ item, position, style }, index) => (
          <motion.div
            key={`desktop-carousel-item-${index}`}
            initial={false}
            animate={{
              x: `${style.xPercent}%`,
              scale: style.scale,
              zIndex: style.zIndex,
              opacity: style.opacity,
              rotateY: position * -5
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 1
            }}
            onAnimationComplete={
              index === currentIndex ? handleAnimationComplete : undefined
            }
            style={{
              position: 'absolute',
              left: items.length === 1 ? '32.5%' : '32%', // Center single items
              top: 0,
              width: '35%',
              height: '100%',
              transformOrigin: 'center center',
              cursor: style.isActive ? 'default' : 'pointer'
            }}
            onClick={() => {
              if (!style.isActive && !isAnimating) {
                setIsAnimating(true);
                setCurrentIndex(index);
              }
            }}
          >
            {renderCard(item, index, style.isActive)}
          </motion.div>
        ))}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            transform: 'translateY(-50%)',
            zIndex: 20,
            px: { xs: 1, sm: 2, md: 3 }
          }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={isAnimating || items.length <= 1}
            sx={{
              bgcolor: 'rgba(255,255,255,0.8)',
              boxShadow: THEME.SHADOWS.MEDIUM,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              color: THEME.COLORS.PRIMARY_BLUE,
              padding: isMobile ? '6px' : '8px'
            }}
          >
            <ArrowBackIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={isAnimating || items.length <= 1}
            sx={{
              bgcolor: 'rgba(255,255,255,0.8)',
              boxShadow: THEME.SHADOWS.MEDIUM,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              color: THEME.COLORS.PRIMARY_BLUE,
              padding: isMobile ? '6px' : '8px'
            }}
          >
            <ArrowForwardIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          pb: '10px',
          position: 'relative',
          zIndex: 1
        }}
      >
        {Array.from({ length: items.length }).map((_, index) => (
          <Box
            key={`desktop-indicator-${index}`}
            sx={{
              width: isMobile ? 8 : 10,
              height: isMobile ? 8 : 10,
              borderRadius: '50%',
              bgcolor:
                index === currentIndex
                  ? THEME.COLORS.PRIMARY_BLUE
                  : THEME.COLORS.NEUTRAL_GRAY_LIGHT,
              mx: 0.5,
              transition: 'background-color 0.3s',
              cursor: 'pointer'
            }}
            onClick={() => {
              if (!isAnimating) {
                setIsAnimating(true);
                setCurrentIndex(index);
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CardCarousel;
